<template>
<div>
    <div class="sm:flex flex-wrap lg:gap-x-12 justify-between">
        <div class="sm:w-1/2 md:w-1/3 xl:w-1/4">
            <div class="flex items-center mb-2">
                <jet-label for="id" value="Recurring Order #:" class="flex w-32 mb-0 leading-7"/>
                <div class="flex mb-1">
                    <Link :href="route('recurring_orders.show', recurringOrder.id)">{{ recurringOrder.id }}</Link>
                </div>
            </div>

            <div class="flex items-start mb-2">
                <jet-label for="schedule_days" value="Schedule Days:" class="flex-none w-40 mb-0 leading-7"/>
                <div class="leading-tight mb-1" v-if="!recurringOrder.schedule_ordinance">{{ asCommaList(recurringOrder.schedule_days).join(', ') }}</div>
                <div class="leading-tight mb-1" v-else>{{ formatOrdinanceWithDays(recurringOrder.schedule_ordinance, recurringOrder.schedule_days) }}</div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="weeks_in_advance" value="Weeks in Advance:" class="flex w-40 mb-0 leading-7"/>
                <div class="flex mb-1 whitespace-nowrap">{{ recurringOrder.weeks_in_advance }}</div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="frequency" value="Frequency:" class="flex w-40 mb-0 leading-7"/>
                <div class="flex mb-1">
                    Every 
                    {{ recurringOrder.schedule_frequency == 1 ? '' : recurringOrder.schedule_frequency}}
                    {{ recurringOrder.schedule_units.slice(0, -1) }}{{ recurringOrder.schedule_frequency > 1 ? 's' : '' }}
                </div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="next_generation_date" value="Next Generation Date:" class="flex w-40 mb-0 leading-7"/>
                <div class="flex mb-1 whitespace-nowrap">
                    {{ formatDate(recurringOrder.next_generation_date) || '—' }}
                </div>
            </div>

            <div class="flex items-center mb-2">
                <jet-label for="created_at" value="Created:" class="flex w-40 mb-0 leading-7"/>
                <div class="flex mb-1 whitespace-nowrap">
                    {{ formatDate(recurringOrder.created_at) }}
                </div>
            </div>

            <div v-if="recurringOrder.shipping_method" class="flex items-center mb-2">
                <jet-label for="shipping_method" value="Shipping Method:" class="flex w-40 mb-0 leading-7"/>
                <div class="flex mb-1 whitespace-nowrap">
                    {{ shippingMethodLabel(recurringOrder.shipping_method) }}
                </div>
            </div>
        </div>

        <div class="sm:w-1/2 md:w-1/3 xl:w-1/4">
            <div class="flex items-center mb-2">
                <jet-label for="customer" value="Customer:" class="flex w-32 sm:w-20 xl:w-28 mb-0 leading-7 self-start" />
                <div class="flex mb-1">
                    {{ recurringOrder.customer?.name}}
                </div>
            </div>
            <div class="flex items-start mb-2">
                <jet-label for="address" value="Address:" class="flex w-32 mb-0 leading-7"/>
                <div class="leading-tight mb-1">
                    <template v-if="recurringOrder.customer">
                        <div>{{ recurringOrder.customer.address_1 }}</div>
                        <div v-show="recurringOrder.customer.address_2">{{ recurringOrder.customer.address_2 }}</div>
                        <div>{{ recurringOrder.customer.city }} {{ recurringOrder.customer.province }}, {{ recurringOrder.customer.postal_code }}</div>
                    </template>
                </div>
            </div>

            <div v-if="recurringOrder.customer?.phone" class="flex items-center mb-2">
                <jet-label for="phone" value="Phone:" class="flex w-32 mb-0 leading-7"/>
                <div class="flex mb-1">
                    {{ recurringOrder.customer.phone }}
                </div>
            </div>

            <div v-if="recurringOrder.customer?.emails?.length" class="flex items-start mb-2">
                <jet-label for="email" value="Email:" class="flex w-32 mb-0 leading-7"/>
                <ul class="leading-tight">
                    <li v-for="email in recurringOrder.customer.emails" :key="email" class="break-all">
                        {{ email }}
                    </li>
                </ul>
            </div>
        </div>

        <div class="sm:w-1/2 md:w-1/3">
            <div v-if="recurringOrder.notes" class="flex items-start mb-2">
                <jet-label for="notes" value="Notes:" class="flex w-32 mb-0 leading-7 whitespace-nowrap"/>
                <div class="flex flex-1 leading-snug mb-1">
                    {{ recurringOrder.notes }}
                </div>
            </div>

            <div v-if="isAdmin && recurringOrder.customer?.internal_notes" class="flex items-start mb-2">
                <jet-label for="internal_notes" value="Internal Notes:" class="flex w-32 mb-0 leading-7 whitespace-nowrap"/>
                <div class="flex flex-1 leading-snug mb-1">
                    {{ recurringOrder.customer.internal_notes }}
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import { usePage, Link } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import JetLabel from '@/Jetstream/Label';
import { DateTime } from "luxon";

export default {
    setup () {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        return { isAdmin };
    },

    components: {
        Link,
        JetLabel,
    },

    data() {
        return {
            imageError: null,
            deletePhotoModal: false,
            previewPhotoModal: false,
        }
    },

    props: ['recurringOrder'],

    methods: {
        shippingMethodLabel(value) {
            if (value === 'pickup') return 'Pickup';
            if (value === 'delivery') return 'Delivery';
            return value;
        },
    },
}
</script>